import styled from '@emotion/styled';
import React from 'react';
import { i18n, SoHealthAdditionalInfo } from 'src/common';
import { PhysicalExaminationEnum, SpecialistExaminationsEnum } from 'src/common/api/apollo/additional/types/types';
import { AmountCard, Flex, RadioCard, Text } from 'src/components';

const AmountOptionsRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  '@media (max-width: 575px)': {
    flexDirection: 'column',
  },
});

const PER_MONTH_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'Do 25€', labelKey: 'up25' },
  { value: 'do 25 - 40€', labelKey: 'range25to40' },
  { value: 'više 40€', labelKey: 'over40' },
  { value: 'Svejedno mi je', labelKey: 'any' },
];

export const emptyHealthExams: SoHealthAdditionalInfo = {
  physicalExamination: null,
  specialistExaminations: false,
  diagnostic: false,
  laboratoryExaminations: null,
  mrCt: false,
  physicalTherapy: false,
  smallOperatingCycles: false,
  reasonOfPurchase: '',
  perMonthPayment: '',
};

/** Error message for an incomplete selection (no exam, or no monthly amount), or '' when valid. */
export const validateHealthExams = (value: SoHealthAdditionalInfo): string => {
  const hasAnyExam =
    value.physicalExamination !== null ||
    value.laboratoryExaminations !== null ||
    value.specialistExaminations ||
    value.diagnostic ||
    value.mrCt ||
    value.physicalTherapy ||
    value.smallOperatingCycles;
  if (!hasAnyExam) return i18n.t('pages.healthExams.errors.noSelection');
  if (!value.perMonthPayment) return i18n.t('pages.healthExams.errors.noAmount');
  return '';
};

interface HealthExamsFormProps {
  value: SoHealthAdditionalInfo;
  onChange: (value: SoHealthAdditionalInfo) => void;
}

/** Dodatno step 2: which exams the customer wants covered and how much per month (controlled). */
export const HealthExamsForm = ({ value, onChange }: HealthExamsFormProps) => {
  const set = (patch: Partial<SoHealthAdditionalInfo>) => onChange({ ...value, ...patch });

  return (
    <>
      <Flex vertical gap={16}>
        <RadioCard
          title={i18n.t('pages.healthExams.exams.systematic.title')}
          description={i18n.t('pages.healthExams.exams.systematic.description')}
          isSelected={value.physicalExamination !== null}
          onClick={() => set({ physicalExamination: value.physicalExamination === null ? PhysicalExaminationEnum.Basic : null })}
        >
          <RadioCard
            title={i18n.t('pages.healthExams.exams.systematic.options.basic.title')}
            description={i18n.t('pages.healthExams.exams.systematic.options.basic.description')}
            isSelected={value.physicalExamination === PhysicalExaminationEnum.Basic}
            onClick={() => set({ physicalExamination: PhysicalExaminationEnum.Basic })}
            isNested
          />
          <RadioCard
            title={i18n.t('pages.healthExams.exams.systematic.options.extended.title')}
            description={i18n.t('pages.healthExams.exams.systematic.options.extended.description')}
            isSelected={value.physicalExamination === PhysicalExaminationEnum.Extended}
            onClick={() => set({ physicalExamination: PhysicalExaminationEnum.Extended })}
            isNested
          />
          <RadioCard
            title={i18n.t('pages.healthExams.exams.systematic.options.any.title')}
            description={i18n.t('pages.healthExams.exams.systematic.options.any.description')}
            isSelected={value.physicalExamination === PhysicalExaminationEnum.NotSpecified}
            onClick={() => set({ physicalExamination: PhysicalExaminationEnum.NotSpecified })}
            isNested
          />
        </RadioCard>

        <RadioCard
          title={i18n.t('pages.healthExams.exams.specialist.title')}
          description={i18n.t('pages.healthExams.exams.specialist.description')}
          isSelected={value.specialistExaminations}
          onClick={() => set({ specialistExaminations: !value.specialistExaminations })}
        />

        <RadioCard
          title={i18n.t('pages.healthExams.exams.diagnostics.title')}
          description={i18n.t('pages.healthExams.exams.diagnostics.description')}
          isSelected={value.diagnostic}
          onClick={() => set({ diagnostic: !value.diagnostic })}
        />

        <RadioCard
          title={i18n.t('pages.healthExams.exams.laboratory.title')}
          description={i18n.t('pages.healthExams.exams.laboratory.description')}
          isSelected={value.laboratoryExaminations !== null}
          onClick={() => set({ laboratoryExaminations: value.laboratoryExaminations === null ? SpecialistExaminationsEnum.Basic : null })}
        >
          <RadioCard
            title={i18n.t('pages.healthExams.exams.laboratory.options.basic.title')}
            description={i18n.t('pages.healthExams.exams.laboratory.options.basic.description')}
            isSelected={value.laboratoryExaminations === SpecialistExaminationsEnum.Basic}
            onClick={() => set({ laboratoryExaminations: SpecialistExaminationsEnum.Basic })}
            isNested
          />
          <RadioCard
            title={i18n.t('pages.healthExams.exams.laboratory.options.extended.title')}
            description={i18n.t('pages.healthExams.exams.laboratory.options.extended.description')}
            isSelected={value.laboratoryExaminations === SpecialistExaminationsEnum.Extended}
            onClick={() => set({ laboratoryExaminations: SpecialistExaminationsEnum.Extended })}
            isNested
          />
        </RadioCard>

        <RadioCard
          title={i18n.t('pages.healthExams.exams.mrct.title')}
          description={i18n.t('pages.healthExams.exams.mrct.description')}
          isSelected={value.mrCt}
          onClick={() => set({ mrCt: !value.mrCt })}
        />

        <RadioCard
          title={i18n.t('pages.healthExams.exams.physical.title')}
          description={i18n.t('pages.healthExams.exams.physical.description')}
          isSelected={value.physicalTherapy}
          onClick={() => set({ physicalTherapy: !value.physicalTherapy })}
        />

        <RadioCard
          title={i18n.t('pages.healthExams.exams.surgery.title')}
          description={i18n.t('pages.healthExams.exams.surgery.description')}
          isSelected={value.smallOperatingCycles}
          onClick={() => set({ smallOperatingCycles: !value.smallOperatingCycles })}
        />
      </Flex>

      <Flex vertical gap={12}>
        <Text type="base" bold>{i18n.t('pages.healthExams.amountTitle')}</Text>
        <AmountOptionsRow>
          {PER_MONTH_OPTIONS.map(({ value: amount, labelKey }) => (
            <AmountCard
              key={labelKey}
              label={i18n.t(`pages.healthExams.amounts.${labelKey}`)}
              isSelected={value.perMonthPayment === amount}
              onClick={() => set({ perMonthPayment: value.perMonthPayment === amount ? '' : amount })}
              hasAnySelection={value.perMonthPayment !== ''}
            />
          ))}
        </AmountOptionsRow>
      </Flex>
    </>
  );
};
